import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
    Bold,
    Code,
    Heading1,
    Heading2,
    Italic,
    Link2,
    List,
    ListOrdered,
    Minus,
    Quote,
    Redo2,
    Strikethrough,
    Underline,
    Undo2,
    Unlink,
} from "lucide-react";
import "../vendor/squire-raw.js"; // side effect: sets window.Squire
import { htmlToPlain, plainToHtml } from "../utils";

export type ComposeMode = "html" | "plain";

export interface ComposeEditorHandle {
    getData: () => string; // HTML when html mode, plain text when plain mode
    getMode: () => ComposeMode;
    setMode: (mode: ComposeMode) => void;
    setContent: (content: string, mode: ComposeMode) => void;
    focus: () => void;
}

export interface ComposeEditorProps {
    initialHtml?: string;
    initialPlain?: string;
    initialMode?: ComposeMode;
    onModeChange?: (mode: ComposeMode) => void;
    onInput?: () => void;
    placeholder?: string;
}

/* Paste-sanitizer (mirrors SnappyMail's SquireDefaultConfig): strip body/html
 * wrappers, comments and empty spans; cleanHtml is a lighter version of what the
 * real client does — DOMPurify already ran on anything we load from the server. */
function sanitizeToDOMFragment(html: string): DocumentFragment {
    const tpl = document.createElement("template");
    let cleaned = (html || "")
        .replace(/<\/?(BODY|HTML)[^>]*>/gi, "")
        .replace(/<!--[^>]+-->/g, "")
        .replace(/<span[^>]*>\s*<\/span>/gi, "")
        .trim();
    /* strip scripts/event handlers on paste (defense in depth) */
    const doc = new DOMParser().parseFromString(cleaned, "text/html");
    doc.querySelectorAll("script, iframe, object, embed, link, meta, style").forEach((n) => n.remove());
    doc.querySelectorAll("*[onclick], *[onerror], *[onload], *[onmouseover]").forEach((n) => {
        for (const attr of [...n.attributes]) {
            if (/^on/i.test(attr.name)) n.removeAttribute(attr.name);
        }
    });
    tpl.innerHTML = doc.body.innerHTML;
    return tpl.content;
}

const TOOLBAR_BTN =
    "flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-white/10 hover:text-white";

export const ComposeEditor = forwardRef<ComposeEditorHandle, ComposeEditorProps>(
    ({ initialHtml = "", initialPlain = "", initialMode = "html", onModeChange, onInput, placeholder }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const plainRef = useRef<HTMLTextAreaElement>(null);
        const squireRef = useRef<Squire | null>(null);
        const [mode, setModeState] = useState<ComposeMode>(initialMode);
        const modeRef = useRef<ComposeMode>(initialMode);
        const plainValueRef = useRef<string>(initialPlain);
        const htmlValueRef = useRef<string>(initialHtml);

        /* init Squire once */
        useEffect(() => {
            if (!containerRef.current) return;
            const squire = new window.Squire(containerRef.current, {
                addLinks: true,
                sanitizeToDOMFragment,
            });
            squireRef.current = squire;
            if (initialHtml) squire.setHTML(initialHtml);
            squire.addEventListener("input", () => {
                htmlValueRef.current = squire.getHTML();
                onInput?.();
            });
            squire.addEventListener("keyup", () => {
                htmlValueRef.current = squire.getHTML();
            });
            return () => {
                squire.destroy();
                squireRef.current = null;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const setMode = (next: ComposeMode) => {
            if (next === modeRef.current) return;
            const squire = squireRef.current;
            if (next === "plain") {
                /* html → plain */
                htmlValueRef.current = squire ? squire.getHTML() : htmlValueRef.current;
                plainValueRef.current = htmlToPlain(htmlValueRef.current);
                containerRef.current?.classList.add("hidden");
                if (plainRef.current) {
                    plainRef.current.value = plainValueRef.current;
                    plainRef.current.classList.remove("hidden");
                }
            } else {
                /* plain → html */
                plainValueRef.current = plainRef.current?.value ?? plainValueRef.current;
                const html = plainToHtml(plainValueRef.current);
                htmlValueRef.current = html;
                plainRef.current?.classList.add("hidden");
                containerRef.current?.classList.remove("hidden");
                squire?.setHTML(html);
            }
            modeRef.current = next;
            setModeState(next);
            onModeChange?.(next);
        };

        useImperativeHandle(ref, () => ({
            getData: () => (modeRef.current === "html" ? htmlValueRef.current : plainRef.current?.value ?? plainValueRef.current),
            getMode: () => modeRef.current,
            setMode,
            setContent: (content, contentMode) => {
                if (contentMode === "html") {
                    htmlValueRef.current = content;
                    if (modeRef.current === "html") squireRef.current?.setHTML(content);
                    else plainValueRef.current = htmlToPlain(content);
                } else {
                    plainValueRef.current = content;
                    if (modeRef.current === "plain") {
                        if (plainRef.current) plainRef.current.value = content;
                    } else htmlValueRef.current = plainToHtml(content);
                }
            },
            focus: () => {
                if (modeRef.current === "html") squireRef.current?.focus();
                else plainRef.current?.focus();
            },
        }));

        const exec = (fn: (s: Squire) => void) => {
            if (!squireRef.current) return;
            fn(squireRef.current);
            htmlValueRef.current = squireRef.current.getHTML();
            onInput?.();
            squireRef.current.focus();
        };

        const askLink = () => {
            const url = window.prompt("Link URL (https://…)");
            if (url) exec((s) => s.makeLink(url));
        };

        return (
            <div className="flex min-h-0 flex-1 flex-col">
                {/* Toolbar */}
                <div className="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-white/10 px-2 py-1.5">
                    <button type="button" className={TOOLBAR_BTN} title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.bold())}>
                        <Bold size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.italic())}>
                        <Italic size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.underline())}>
                        <Underline size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Strikethrough" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.strikethrough())}>
                        <Strikethrough size={14} />
                    </button>

                    <div className="mx-1 h-4 w-px bg-white/10" />

                    <button type="button" className={TOOLBAR_BTN} title="Heading 1" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.toggleTag("h1"))}>
                        <Heading1 size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Heading 2" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.toggleTag("h2"))}>
                        <Heading2 size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.makeUnorderedList())}>
                        <List size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.makeOrderedList())}>
                        <ListOrdered size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Quote" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.increaseQuoteLevel())}>
                        <Quote size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Code" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.toggleCode())}>
                        <Code size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Horizontal rule" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.insertHTML("<hr>"))}>
                        <Minus size={14} />
                    </button>

                    <div className="mx-1 h-4 w-px bg-white/10" />

                    <button type="button" className={TOOLBAR_BTN} title="Insert link" onMouseDown={(e) => e.preventDefault()} onClick={askLink}>
                        <Link2 size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Remove link" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.removeLink())}>
                        <Unlink size={14} />
                    </button>

                    <div className="mx-1 h-4 w-px bg-white/10" />

                    <button type="button" className={TOOLBAR_BTN} title="Undo" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.undo())}>
                        <Undo2 size={14} />
                    </button>
                    <button type="button" className={TOOLBAR_BTN} title="Redo" onMouseDown={(e) => e.preventDefault()} onClick={() => exec((s) => s.redo())}>
                        <Redo2 size={14} />
                    </button>

                    {/* HTML/plain toggle */}
                    <div className="ml-auto flex items-center rounded-lg border border-white/10 bg-black/20 p-0.5">
                        {(["html", "plain"] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setMode(m)}
                                className={`cursor-pointer rounded-md px-2.5 py-1 text-[10px] font-black uppercase tracking-widest transition-colors ${
                                    mode === m ? "bg-orange-500/20 text-orange-300" : "text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Editor surfaces */}
                <div
                    ref={containerRef}
                    className={`squire-editor min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[13.5px] leading-relaxed text-zinc-100 outline-none [&_a]:text-orange-400 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-white/15 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-400 [&_div]:my-1 ${mode !== "html" ? "hidden" : ""}`}
                    contentEditable
                    data-placeholder={placeholder}
                />
                <textarea
                    ref={plainRef}
                    defaultValue={initialPlain}
                    onChange={(e) => {
                        plainValueRef.current = e.target.value;
                        onInput?.();
                    }}
                    placeholder={placeholder}
                    className={`custom-scrollbar min-h-0 flex-1 resize-none bg-transparent px-4 py-3 font-mono text-[13px] leading-relaxed text-zinc-100 placeholder:text-zinc-600 focus:outline-none ${mode !== "plain" ? "hidden" : ""}`}
                />
            </div>
        );
    }
);

ComposeEditor.displayName = "ComposeEditor";
